import React from 'react';
import { Link } from 'react-router-dom';
import LightPull from '../components/LightPull';
import useRoom from '../theme/useRoom';
import { LIVE, WORK, SKILLS, EXPERIENCE, STRIP } from '../data/projects';
import './Home.css';

const EMAIL = 'caleb.a.lykken@gmail.com';
const LINKEDIN = 'https://linkedin.com/in/caleblykken';
const asset = (p) => `${process.env.PUBLIC_URL}${p}`;

export default function Home() {
  const { lit, toggle } = useRoom();

  return (
    <div className="pf">
      <header className="pf__bar">
        <div className="pf__barinner">
          <span className="pf__mark">Caleb Lykken</span>
          <nav className="pf__nav">
            <a href="#work">Work</a>
            <a href="#stack">Stack</a>
            <a href="#experience">Experience</a>
            <Link to="/darkroom">Darkroom</Link>
          </nav>
        </div>
      </header>

      <LightPull
        on={lit}
        onToggle={toggle}
        hint={lit ? 'Switch to safelight' : 'Switch the room light on'}
      />

      <main className="pf__main">
        {/* ---------------------------------------------------------- hero */}
        <section className="pf__hero">
          <p className="t-edge">
            Seattle, WA<i>·</i>Software Engineer<i>·</i>Data Science
          </p>
          <h1 className="t-display pf__name">Caleb Lykken</h1>
          <p className="t-lead pf__pitch">
            I build the model and then I build the thing that uses it. Half my work is
            data — pipelines, prediction, the analysis underneath a decision. The other
            half is shipping the application that puts it in someone&rsquo;s hands.
            The projects I care about are the ones that need both.
          </p>
          <p className="t-body pf__now">
            Currently a software engineer at{' '}
            <strong>Hackerant Industries</strong> in Redmond. Previously data at{' '}
            <strong>Seattle City Light</strong>, and a BS in Information Science &amp;
            Data Science from the <strong>University of Washington</strong>.
          </p>
          <p className="pf__links">
            <a className="pf__link" href={`mailto:${EMAIL}`}>{EMAIL}</a>
            <a className="pf__link" href={LINKEDIN} target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a>
          </p>
        </section>

        {/* ------------------------------------------------- things you can use */}
        <section className="pf__section" id="work">
          <div className="pf__head">
            <h2 className="t-h2">Open these</h2>
            <span className="t-edge">Running here, in the browser</span>
          </div>

          <div className="pf__live">
            {LIVE.map((p) => (
              <Link key={p.title} to={p.to} className="pf__card pf__card--live">
                <span className="pf__shot">
                  <img src={asset(p.image)} alt="" loading="lazy" />
                </span>
                <span className="pf__cardbody">
                  <span className="pf__cardtop">
                    <span className="t-h3">{p.title}</span>
                    <span className="t-edge">{p.year}</span>
                  </span>
                  <span className="t-body pf__summary">{p.summary}</span>
                  <span className="t-edge pf__stack">{p.stack.join(' · ')}</span>
                  <span className="pf__action">{p.action} →</span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* ----------------------------------------------------- described work */}
        <section className="pf__section">
          <div className="pf__head">
            <h2 className="t-h2">Selected work</h2>
            <span className="t-edge">{WORK.length} projects</span>
          </div>

          <div className="pf__grid">
            {WORK.map((p) => (
              <article key={p.title} className="pf__card">
                <div className="pf__cardtop">
                  <h3 className="t-h3">{p.title}</h3>
                  <span className="t-edge">{p.year}</span>
                </div>
                <p className="t-body pf__summary">{p.summary}</p>
                <p className="t-edge pf__stack">{p.stack.join(' · ')}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------------ stack */}
        <section className="pf__section" id="stack">
          <div className="pf__head">
            <h2 className="t-h2">Stack</h2>
            <span className="t-edge">What I reach for</span>
          </div>

          <dl className="pf__spec">
            {SKILLS.map((s) => (
              <div key={s.label} className="pf__specrow">
                <dt className="t-edge">{s.label}</dt>
                <dd className="t-edge pf__specval">{s.items.join(' · ')}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* ------------------------------------------------------- experience */}
        <section className="pf__section" id="experience">
          <div className="pf__head">
            <h2 className="t-h2">Experience</h2>
          </div>

          <ol className="pf__timeline">
            {EXPERIENCE.map((e) => (
              <li key={`${e.org}-${e.when}`} className="pf__job">
                <div className="pf__jobwhen t-edge">{e.when}</div>
                <div className="pf__jobbody">
                  <h3 className="t-h3">
                    {e.role}
                    {e.current && <span className="pf__dot" aria-label="current role" />}
                  </h3>
                  <p className="t-edge">{e.org}<i>·</i>{e.place}</p>
                  <p className="t-body pf__summary">{e.note}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ------------------------------------------------------ photography */}
        <section className="pf__section pf__photo">
          <div className="pf__head">
            <h2 className="t-h2">Off the clock</h2>
            <span className="t-edge">35mm black &amp; white</span>
          </div>
          <p className="t-body pf__summary pf__photoblurb">
            I shoot film and print it myself. The same attention it takes to get a frame
            right is the attention I bring to everything above.
          </p>
          <Link to="/darkroom" className="pf__strip" aria-label="Open the darkroom">
            {STRIP.map((f) => (
              <span key={f} className="pf__frame">
                <img src={asset(`/photos/thumb/${f}.jpg`)} alt="" loading="lazy" />
              </span>
            ))}
          </Link>
          <Link to="/darkroom" className="pf__action pf__action--standalone">
            Open the darkroom →
          </Link>
        </section>
      </main>

      <footer className="pf__foot">
        <span className="t-edge">Caleb Lykken<i>·</i>Seattle, WA</span>
        <span className="pf__footlinks">
          <a className="pf__link" href={`mailto:${EMAIL}`}>Email</a>
          <a className="pf__link" href={LINKEDIN} target="_blank" rel="noopener noreferrer">
            LinkedIn
          </a>
        </span>
      </footer>
    </div>
  );
}
